// The schedule priced: the adapters over `core/src/teaching.rs` (#470).
//
// The day's four numbers ("Today at a glance", DayView.jsx) and a forecast
// month's teaching income (the trend's dashed months, useExpenses.jsx) are the
// core's folds, and every client asks the same one. What is here is the
// crossing: a lesson's fields read, the students on it keyed by position, and
// a student's pricing looked up once.

import { coreSync } from './core.js';

// `Number(v)` for a stored figure, and absent for null and undefined — which
// both folds read as nothing to price from.
const num = (v) => (v == null ? undefined : Number(v));
const text = (v) => (v == null ? undefined : String(v));

function lessonOf(o) {
  return {
    durationMin: num(o.durationMin),
    allDay: o.allDay === true,
    requiresMakeupCredit: o.requiresMakeupCredit === true,
    pricingRule: text(o.pricingRule),
    priceOverrideMinor: typeof o.priceOverrideMinor === 'number' ? o.priceOverrideMinor : undefined,
  };
}

function studentsOf(ids, studentById) {
  const out = [];
  for (const id of new Set(ids)) {
    const s = studentById(id);
    if (s) out.push({ id, pricingMode: text(s.pricingMode), rateMinor: num(s.rateMinor) });
  }
  return out;
}

// The day list's lessons, as `useCalendarOccurrences` flattens them — one row
// per attendee, one for a lesson nobody is on — into the card's four numbers:
// `{ lessonCount, teachingHours, teachingHoursText, attendedCount, billedMinor }`.
// A flattened row is its own lesson here, with its student (if any) on it.
export function dayGlance(rows, getStudent) {
  const lessons = [];
  const attendees = [];
  (rows || []).forEach((l, i) => {
    lessons.push(lessonOf({ ...l, priceOverrideMinor: l.priceMinor }));
    if (l.studentId) attendees.push({ lesson: i, studentId: String(l.studentId), status: text(l.status) });
  });
  return coreSync().teachingDayGlance(
    lessons, attendees, studentsOf(attendees.map((a) => a.studentId), getStudent),
  );
}

// A month's scheduled occurrences priced as the forecast prices them.
// `statusFor(occurrenceId, studentId)` is the attendance recorded, or null.
export function projectTeaching(occurrences, studentById, statusFor) {
  const lessons = [];
  const attendees = [];
  (occurrences || []).forEach((o, i) => {
    lessons.push(lessonOf(o));
    for (const sid of o.studentIds || []) {
      attendees.push({ lesson: i, studentId: String(sid), status: text(statusFor(o.id, sid)) });
    }
  });
  return coreSync().teachingForecast(
    lessons, attendees, studentsOf(attendees.map((a) => a.studentId), studentById),
  );
}
